import { useState, useEffect } from 'react'
import { addExpense, getBudgets, getExpenses, getCustomCategories, getCustomModes } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { DEFAULT_CATEGORIES, DEFAULT_MODES, CATEGORY_COLORS } from '../lib/constants'

function calcTotal(parts) {
  return parts.reduce((sum, p) => sum + (parseFloat(p) || 0), 0)
}

export default function AddPage({ onAdded, catsVersion }) {
  const { user } = useAuth()
  const [allCats, setAllCats]   = useState(DEFAULT_CATEGORIES)
  const [allModes, setAllModes]  = useState(DEFAULT_MODES)
  const [parts, setParts]       = useState([''])
  const [cat, setCat]           = useState(DEFAULT_CATEGORIES[0].label)
  const [mode, setMode]         = useState(DEFAULT_MODES[0].label)
  const [note, setNote]         = useState('')
  const [date, setDate]         = useState(new Date().toISOString().split('T')[0])
  const [saving, setSaving]     = useState(false)
  const [nudge, setNudge]       = useState(null)
  const [success, setSuccess]   = useState(false)
  const [confirm, setConfirm]   = useState(false)

  useEffect(() => {
    if (!user) return
    getCustomCategories(user.id).then(custom => {
      setAllCats([...DEFAULT_CATEGORIES, ...custom.map(c => ({ label: c.label, icon: c.icon, color: c.color }))])
    })
    getCustomModes(user.id).then(custom => {
      setAllModes([...DEFAULT_MODES, ...custom.map(m => ({ label: m.label, icon: m.icon, color: '#555555' }))])
    })
  }, [user, catsVersion])

  const total = calcTotal(parts)
  const hasMultiple = parts.length > 1

  const updatePart = (i, val) => { const n = [...parts]; n[i] = val; setParts(n) }
  const addPart    = () => setParts(p => [...p, ''])
  const removePart = (i) => { if (parts.length === 1) return; setParts(p => p.filter((_, idx) => idx !== i)) }

  const requestSave = () => { if (!total || total <= 0) return; setConfirm(true) }

  const doSave = async () => {
    setConfirm(false); setSaving(true)
    try {
      await addExpense({ amount: total, category: cat, mode, note, date, userId: user.id, householdId: null })
      setParts(['']); setNote('')
      setSuccess(true)
      setTimeout(() => setSuccess(false), 2500)
      try {
        const budgets = await getBudgets(user.id, null)
        const limit = budgets[cat]
        if (limit) {
          const now = new Date(date)
          const exps = await getExpenses({ userId: user.id, householdId: null, month: now.getMonth() + 1, year: now.getFullYear() })
          const spent = exps.filter(e => e.category === cat).reduce((s, e) => s + e.amount, 0)
          const pct = spent / limit
          if (pct >= 1) setNudge({ type: 'over', cat, spent, limit })
          else if (pct >= 0.8) setNudge({ type: 'warn', cat, spent, limit, pct })
          else setNudge(null)
        } else setNudge(null)
      } catch (_) {}
      if (onAdded) onAdded()
    } catch (e) { alert('Error saving: ' + e.message) }
    setSaving(false)
  }

  return (
    <div style={s.wrap}>
      {/* Confirmation popup */}
      {confirm && (
        <div style={s.overlay}>
          <div style={s.popup}>
            <div style={s.popupTitle}>Confirm expense</div>
            <div style={s.popupAmt}>₹{total.toLocaleString('en-IN')}</div>
            {hasMultiple && (
              <div style={s.popupBreakdown}>
                {parts.filter(p => parseFloat(p) > 0).map((p, i, arr) => (
                  <span key={i}>₹{parseFloat(p).toLocaleString('en-IN')}{i < arr.length-1 ? ' + ' : ''}</span>
                ))}
              </div>
            )}
            <div style={s.popupDetail}>{cat} · {mode}</div>
            {note ? <div style={s.popupNote}>"{note}"</div> : null}
            <div style={s.popupBtns}>
              <button style={s.cancelBtn} onClick={() => setConfirm(false)}>Cancel</button>
              <button style={s.confirmBtn} onClick={doSave}>Yes, save</button>
            </div>
          </div>
        </div>
      )}

      {nudge && (
        <div style={{ ...s.nudge, background: nudge.type === 'over' ? '#FDF0EE' : '#FEF9EC', borderColor: nudge.type === 'over' ? '#E8927A' : '#F5C842' }}>
          <span style={{ fontSize: 18 }}>{nudge.type === 'over' ? '⚠️' : '!'}</span>
          <span style={{ fontSize: 14, color: nudge.type === 'over' ? '#8B2500' : '#7A5C00' }}>
            {nudge.type === 'over'
              ? `${nudge.cat} budget crossed — ₹${Math.round(nudge.spent).toLocaleString('en-IN')} of ₹${Math.round(nudge.limit).toLocaleString('en-IN')}`
              : `${nudge.cat} at ${Math.round(nudge.pct*100)}% — ₹${Math.round(nudge.limit-nudge.spent).toLocaleString('en-IN')} left`}
          </span>
          <button onClick={() => setNudge(null)} style={s.nudgeClose}>×</button>
        </div>
      )}

      {success && <div style={s.success}>✓ Saved ₹{total.toLocaleString('en-IN')}</div>}

      {/* Amount */}
      <div style={s.amtWrap}>
        {parts.map((p, i) => (
          <div key={i} style={s.partRow}>
            {i > 0 && <span style={s.plusSign}>+</span>}
            <span style={s.rupee}>₹</span>
            <input style={s.amtInput} type="number" inputMode="decimal" placeholder="0"
              value={p} onChange={e => updatePart(i, e.target.value)} autoFocus={i === parts.length-1} />
            {parts.length > 1 && <button style={s.removeBtn} onClick={() => removePart(i)}>×</button>}
          </div>
        ))}
        <button style={s.addPartBtn} onClick={addPart}>+ Add another item</button>
        {hasMultiple && total > 0 && (
          <div style={s.totalRow}>Total = <strong style={{ marginLeft: 6 }}>₹{total.toLocaleString('en-IN')}</strong></div>
        )}
      </div>

      {/* Category */}
      <div style={s.section}>
        <div style={s.label}>Category</div>
        <div style={s.grid}>
          {allCats.map(c => (
            <button key={c.label} style={{ ...s.chip, ...(cat === c.label ? { background: c.color, color: '#fff', borderColor: c.color } : {}) }}
              onClick={() => setCat(c.label)}>
              <span style={{ fontSize: 16 }}>{c.icon}</span>
              <span style={{ fontSize: 10, marginTop: 2, textAlign: 'center', lineHeight: 1.2 }}>{c.label.split(' ')[0]}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Payment mode */}
      <div style={s.section}>
        <div style={s.label}>Payment mode</div>
        <div style={s.modeRow}>
          {allModes.map(m => (
            <button key={m.label} style={{ ...s.modeBtn, ...(mode === m.label ? { background: m.color, color: '#fff', borderColor: m.color } : {}) }}
              onClick={() => setMode(m.label)}>
              {m.icon} {m.label}
            </button>
          ))}
        </div>
      </div>

      <div style={s.row}>
        <div style={{ flex: 1 }}>
          <div style={s.label}>Date</div>
          <input style={s.input} type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <div style={{ flex: 2, marginLeft: 10 }}>
          <div style={s.label}>Note (optional)</div>
          <input style={s.input} placeholder="e.g. Swiggy, petrol…" value={note} onChange={e => setNote(e.target.value)} />
        </div>
      </div>

      <button style={{ ...s.saveBtn, opacity: saving ? 0.6 : 1 }} onClick={requestSave} disabled={saving}>
        {saving ? 'Saving…' : `Save ₹${total > 0 ? total.toLocaleString('en-IN') : '—'}`}
      </button>
    </div>
  )
}

const s = {
  wrap:          { padding: '1rem' },
  overlay:       { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '1rem' },
  popup:         { background: '#fff', borderRadius: 20, padding: '2rem 1.5rem', width: '100%', maxWidth: 340, textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' },
  popupTitle:    { fontSize: 13, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 },
  popupAmt:      { fontSize: 42, fontWeight: 700, color: '#1A1A2E', marginBottom: 6 },
  popupBreakdown:{ fontSize: 13, color: '#888', marginBottom: 8 },
  popupDetail:   { fontSize: 15, color: '#555', marginBottom: 4 },
  popupNote:     { fontSize: 13, color: '#888', fontStyle: 'italic', marginBottom: 16 },
  popupBtns:     { display: 'flex', gap: 10, marginTop: 20 },
  cancelBtn:     { flex: 1, padding: '12px', background: '#F7F4EF', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: 'pointer', color: '#555' },
  confirmBtn:    { flex: 1, padding: '12px', background: '#1A1A2E', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer', color: '#E8C547' },
  nudge:         { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 12, border: '1.5px solid', marginBottom: 16 },
  nudgeClose:    { marginLeft: 'auto', background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' },
  success:       { background: '#EAFAF1', color: '#1E8449', padding: '10px 16px', borderRadius: 10, marginBottom: 14, fontSize: 14, fontWeight: 600 },
  amtWrap:       { background: '#1A1A2E', borderRadius: 16, padding: '1.2rem 1.5rem', marginBottom: 20 },
  partRow:       { display: 'flex', alignItems: 'center', marginBottom: 8 },
  plusSign:      { color: '#E8C547', fontSize: 24, fontWeight: 700, marginRight: 8 },
  rupee:         { fontSize: 28, color: '#E8C547', fontWeight: 700, marginRight: 6 },
  amtInput:      { flex: 1, background: 'none', border: 'none', outline: 'none', fontSize: 36, fontWeight: 700, color: '#fff', width: '100%', fontFamily: 'inherit' },
  removeBtn:     { background: 'none', border: 'none', color: '#E8C547', fontSize: 22, cursor: 'pointer', padding: '0 4px', opacity: 0.7 },
  addPartBtn:    { background: 'none', border: '1px dashed #E8C547', borderRadius: 8, color: '#E8C547', fontSize: 13, fontWeight: 600, padding: '6px 14px', cursor: 'pointer', marginTop: 4, width: '100%' },
  totalRow:      { marginTop: 10, fontSize: 16, color: '#E8C547', paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.1)' },
  section:       { marginBottom: 18 },
  label:         { fontSize: 12, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 },
  grid:          { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 },
  chip:          { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 4px', border: '1.5px solid #E8E4DC', borderRadius: 12, background: '#FAFAF8', cursor: 'pointer', gap: 4, transition: 'all 0.15s', color: '#555' },
  modeRow:       { display: 'flex', flexWrap: 'wrap', gap: 8 },
  modeBtn:       { padding: '8px 14px', border: '1.5px solid #E8E4DC', borderRadius: 20, background: '#FAFAF8', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#555', transition: 'all 0.15s' },
  row:           { display: 'flex', gap: 0, marginBottom: 20 },
  input:         { width: '100%', padding: '10px 12px', border: '1.5px solid #E8E4DC', borderRadius: 10, fontSize: 14, background: '#FAFAF8', color: '#1A1A2E', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' },
  saveBtn:       { width: '100%', padding: 15, background: '#1A1A2E', color: '#E8C547', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.3 },
}
